import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Printer, Pencil, Trash2, Search, Calculator, ChevronRight, X } from "lucide-react";
import { VehicleCopiesBatch, printBatch } from "@/components/VehicleCopiesBatch";
import { MoreMenu } from "@/components/MoreMenu";

const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

const JobRow = ({ j, selected, toggle, remove }) => (
  <div className={`rounded-xl border bg-[#181A22] flex items-center gap-3 pr-2 transition-colors ${selected ? "border-[#E63946]/60" : "border-white/10 hover:border-white/25"}`} data-testid={`bodyshop-row-${j.id}`}>
    <input type="checkbox" checked={selected} onChange={() => toggle(j.id)} className="ml-4 w-6 h-6 shrink-0 accent-[#E63946] cursor-pointer" aria-label="Seleziona per la stampa" data-testid={`bodyshop-select-${j.id}`} />
    <Link to={`/admin/carrozzeria/${j.id}`} className="min-w-0 flex-1 flex items-center gap-3 p-4 pr-1" data-testid={`bodyshop-open-${j.id}`}>
      <div className="hidden sm:block font-mono-tech text-xs bg-white/5 rounded px-2 py-1 shrink-0">{j.number || "—"}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">{j.plate || "Senza targa"} · {j.brand} {j.model}{j.status === "closed" && <span className="ml-2 text-[10px] uppercase font-bold bg-[#16A34A]/20 text-[#4ADE80] rounded-full px-2 py-0.5" data-testid={`bodyshop-closed-${j.id}`}>Fine lavoro</span>}</div>
        <div className="text-xs text-muted-foreground truncate"><span className="sm:hidden">{j.number ? `N. ${j.number} · ` : ""}</span>{j.customer_name || "Cliente non indicato"} · ingresso {fmt(j.date_in)}{j.date_out ? ` · consegna ${fmt(j.date_out)}` : ""}</div>
      </div>
      <ChevronRight className="hidden sm:block w-4 h-4 text-muted-foreground shrink-0" />
    </Link>
    <button onClick={() => remove(j)} className="p-2 rounded-full text-muted-foreground hover:text-[#F87171] hover:bg-[#E63946]/15 transition-colors" aria-label="Elimina pratica" data-testid={`bodyshop-delete-${j.id}`}><Trash2 className="w-4 h-4" /></button>
    <MoreMenu testid={`bodyshop-more-${j.id}`} label="Altro" items={[
      { label: "Modifica scheda", icon: Pencil, to: `/admin/carrozzeria/${j.id}?modifica=1`, testid: `bodyshop-menu-open-${j.id}` },
      { label: "Stampa copia veicolo", icon: Printer, to: `/admin/carrozzeria/${j.id}?stampa=1`, testid: `bodyshop-print-${j.id}` },
      { label: "Consuntivo", icon: Calculator, to: `/admin/carrozzeria/${j.id}/consuntivo`, testid: `bodyshop-consuntivo-${j.id}` },
    ]} />
  </div>
);

export const JobsList = () => {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [st, setSt] = useState("open");
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const load = (query) => api.get("/admin/bodyshop", { params: query ? { q: query } : {} }).then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);
  const remove = async (j) => {
    if (!window.confirm(`Eliminare la scheda ${j.number || j.plate}?`)) return;
    try { await api.delete(`/admin/bodyshop/${j.id}`); toast.success("Scheda eliminata"); load(q); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const stopSelecting = () => setSel([]);
  const count = (k) => (jobs || []).filter((j) => k === "all" || (k === "closed") === (j.status === "closed")).length;
  const shown = (jobs || []).filter((j) => st === "all" || (st === "closed") === (j.status === "closed")).sort((a, b) => (a.status === "closed") - (b.status === "closed"));

  return (
    <div data-testid="bodyshop-tab">
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa, cliente, n. scheda" className="pl-9 h-10 bg-[#181A22] border-white/10" data-testid="bodyshop-search" />
      </div>
      <div className="inline-flex gap-1 rounded-full border border-white/10 bg-[#12141A] p-1 mb-4" data-testid="bodyshop-status-filter">
        {[["open", "In lavorazione"], ["closed", "Finite"], ["all", "Tutte"]].map(([k, l]) => (
          <button key={k} onClick={() => setSt(k)} data-testid={`bodyshop-filter-${k}`}
            className={`rounded-full px-3 sm:px-4 py-1.5 text-sm transition-colors ${st === k ? "bg-white text-black font-semibold" : "text-muted-foreground hover:text-white"}`}>
            {l} <span className="opacity-60">({count(k)})</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-2" data-testid="bodyshop-select-hint">Spunta le schede da stampare: due copie veicolo per foglio.</p>
      {jobs === null ? (
        <p className="text-sm text-muted-foreground" data-testid="bodyshop-loading">Caricamento…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="bodyshop-empty">Nessuna scheda{q || st !== "all" ? " in questo elenco" : ": crea la prima con \"Nuova\""}.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((j) => <JobRow key={j.id} j={j} selected={sel.includes(j.id)} toggle={toggle} remove={remove} />)}
        </div>
      )}
      {sel.length > 0 && (
        <div className="sticky bottom-24 md:bottom-4 mt-4 z-20 flex items-center gap-2 rounded-full bg-[#12141A]/95 backdrop-blur border border-[#E63946]/50 pl-4 pr-2 py-2 shadow-2xl" data-testid="bodyshop-print-bar">
          <span className="text-sm font-semibold mr-auto">{sel.length} {sel.length === 1 ? "veicolo" : "veicoli"} · {Math.ceil(sel.length / 2)} {Math.ceil(sel.length / 2) === 1 ? "foglio" : "fogli"}</span>
          <Button size="sm" variant="ghost" onClick={stopSelecting} className="rounded-full" data-testid="bodyshop-clear-selection" aria-label="Annulla"><X className="w-4 h-4" /></Button>
          <Button size="sm" onClick={printBatch} disabled={!sel.length} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="bodyshop-print-selected"><Printer className="w-4 h-4 mr-1.5" />{sel.length === 2 ? "Stampa 2 in un foglio" : "Stampa"}</Button>
        </div>
      )}
      <VehicleCopiesBatch jobs={(jobs || []).filter((j) => sel.includes(j.id))} />
    </div>
  );
};
